import React, { useEffect, useMemo, useState } from 'react';
import TextField from 'components/Form/TextField';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Button from 'components/Form/Button';
import Spacer from 'components/Spacer';
import { Icon, Icons } from 'components/Icon';
import { GuardianTypeEnum } from '../types/user.enum.type';
import CustomSelect from 'components/Form/CustomSelect';
import { RelationshipType } from 'constants/bank-account-type';
import { MaritalStatus } from 'constants/marital-status';
import {
  useCreateGuardian,
  useDeleteGuardian,
  useGetGuardianList
} from 'common/queries-and-mutations/guardian';
import { Loader } from 'components/Loader';
import { findNextPage, findPreviousPage, showRemoveButton } from 'utils';
import { GuardianType } from '../types/on-boarding.type';
import InfoBar from './InfoBar';
import { STEPS } from 'constants/steps';
import { fetchUserInformation } from 'thunks/account-thunk';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectAccountDetails } from 'selectors/account-selector';

export default function GuardianInfo() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLater, setIsLater] = useState(false);

  const { mutate, isSuccess, isLoading, isError } = useCreateGuardian();
  const { mutate: deleteGuardian, isLoading: isDeleting } = useDeleteGuardian();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(selectAccountDetails);
  const planAllowPage = user && JSON.parse(user?.planAllowPage);

  const parts = window.location.pathname.split('/');
  const parentPath = parts[1];

  const {
    data,
    isSuccess: isGuardianSuccess,
    isLoading: isGuardianLoading
  } = useGetGuardianList();

  const guardians = useMemo(() => {
    if (isGuardianSuccess && data.length > 0) {
      return data.map(guaridan => ({
        guardianId: guaridan.id,
        guardianFirstName: guaridan.guardianFirstName,
        guardianMiddleName: guaridan.guardianMiddleName,
        guardianLastName: guaridan.guardianLastName,
        guardianType: guaridan.guardianType,
        relationship: guaridan.relationship,
        maritalStatus: guaridan.maritalStatus,
        address: guaridan.address,
        emailAddress: guaridan.emailAddress,
        phoneNumber: guaridan.phoneNumber,
        dateOfBirth: guaridan.dateOfBirth
      }));
    } else {
      return [
        {
          guardianId: null,
          guardianFirstName: '',
          guardianMiddleName: '',
          guardianLastName: '',
          guardianType: '',
          relationship: '',
          maritalStatus: '',
          address: '',
          emailAddress: '',
          phoneNumber: '',
          dateOfBirth: ''
        }
      ];
    }
  }, [data, isGuardianSuccess]);

  const formik = useFormik({
    initialValues: {
      guardians
    },
    validationSchema: Yup.object({
      guardians: Yup.array().of(
        Yup.object().shape({
          guardianFirstName: Yup.string()
            .required('Guardian first name is required')
            .nullable(),
          // guardianMiddleName: Yup.string()
          //   .required('Guardian middle name is required')
          //   .nullable(),
          guardianLastName: Yup.string()
            .required('Guardian last name is required')
            .nullable(),
          guardianType: Yup.string()
            .required('Guardian type is required')
            .nullable(),
          relationship: Yup.string()
            .required('Relationship is required')
            .nullable(),
          maritalStatus: Yup.string()
            .required('Marital status is required')
            .nullable(),
          address: Yup.string().required('Home address is required').nullable(),
          emailAddress: Yup.string()
            .email('Invalid email address')
            .required('Email is required')
            .nullable(),
          phoneNumber: Yup.string()
            .required('Phone number is required')
            .nullable(),
          dateOfBirth: Yup.date()
            .required('Date of Birth is required')
            .nullable()
        })
      )
    }),
    enableReinitialize: true,
    onSubmit: (values, { setFieldTouched }) => {
      setIsSubmitting(true);
      for (const key in values) {
        setFieldTouched(key, true);
      }
      onGuardianSubmit(values);
    }
  });

  const addMoreGuardian = () => {
    formik.values.guardians.push({
      guardianId: null,
      guardianFirstName: '',
      guardianMiddleName: '',
      guardianLastName: '',
      guardianType: '',
      relationship: '',
      maritalStatus: '',
      address: '',
      emailAddress: '',
      phoneNumber: '',
      dateOfBirth: ''
    });
    formik.setFieldValue('guardians', [...formik.values.guardians]);
  };

  const removeGuardian = (
    indexToRemove: number,
    guardianId?: number | null
  ) => {
    if (guardianId) {
      deleteGuardian(guardianId);
    }
    formik.values.guardians.splice(indexToRemove, 1);
    formik.setFieldValue('guardians', [...formik.values.guardians]);
  };

  const onGuardianSubmit = (formValue: GuardianType) => {
    mutate(formValue);
  };

  useEffect(() => {
    if (isSuccess && !isError) {
      setIsSubmitting(false);
      dispatch(fetchUserInformation());
      if (isLater) {
        navigate('/dashboard');
        setIsLater(false);
        return;
      }
      navigate(findNextPage(planAllowPage, STEPS.GUARDIAN_INFORMATION));
    }
    if (isError) {
      setIsSubmitting(false);
    }
  }, [isSuccess, isError, dispatch, navigate, planAllowPage]);

  const onContinueLater = () => {
    if (Object.keys(formik.errors).length) {
      return;
    }
    mutate(formik.values);
  };

  const onPrevious = () => {
    navigate(findPreviousPage(planAllowPage, STEPS.GUARDIAN_INFORMATION));
  };

  return (
    <div className="mx-10">
      <InfoBar
        stepCounter={1}
        stepPercentage={STEPS.GUARDIAN_INFORMATION}
        timelineStep={3}
      />
      {isGuardianLoading ? (
        <div className="mt-40 flex flex-col items-center justify-center">
          <Loader color={'#FF6606'} height={50} width={50} />
        </div>
      ) : (
        <>
          <p className="my-9 text-center font-circular text-lg font-medium text-[#353535]">
            Guardian&apos;s Information
          </p>
          <form
            autoComplete="false"
            className="m-10"
            onSubmit={formik.handleSubmit}
          >
            {formik.values.guardians.map((guardian, index) => (
              <div key={index} className="mb-1 mt-8">
                <div className="grid md:grid-cols-2 md:gap-10">
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`guardians[${index}].guardianFirstName`}
                      placeholder=""
                      value={guardian.guardianFirstName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Guardian First Name"
                      error={
                        formik.touched.guardians
                          ? formik.errors.guardians?.[index] &&
                            formik.errors.guardians.length > 0
                            ? JSON.parse(
                                JSON.stringify(formik.errors.guardians[index])
                              ).guardianFirstName
                            : ''
                          : ''
                      }
                      isRequired
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`guardians[${index}].guardianMiddleName`}
                      placeholder=""
                      value={guardian.guardianMiddleName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Guardian Middle Name"
                      error={
                        formik.touched.guardians
                          ? formik.errors.guardians?.[index] &&
                            formik.errors.guardians.length > 0
                            ? JSON.parse(
                                JSON.stringify(formik.errors.guardians[index])
                              ).guardianMiddleName
                            : ''
                          : ''
                      }
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 md:gap-10">
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`guardians[${index}].guardianLastName`}
                      placeholder=""
                      value={guardian.guardianLastName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Guardian Last Name"
                      error={
                        formik.touched.guardians
                          ? formik.errors.guardians?.[index] &&
                            formik.errors.guardians.length > 0
                            ? JSON.parse(
                                JSON.stringify(formik.errors.guardians[index])
                              ).guardianLastName
                            : ''
                          : ''
                      }
                      isRequired
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                  <div className="group relative z-0 mb-6 w-full">
                    <div className="relative mb-2 flex items-center justify-between">
                      <span className="flex items-center">
                        <label className="text-[12px] font-medium text-[#353535]">
                          Type of Guardian
                        </label>
                        <div className="mx-2 mb-2">
                          <Spacer width={3} />
                          <Icon name={Icons.Required} />
                        </div>
                      </span>
                    </div>
                    <div className="mt-5 flex items-center gap-10">
                      <button
                        type="button"
                        onClick={() =>
                          formik.setFieldValue(
                            `guardians[${index}].guardianType`,
                            GuardianTypeEnum.PRIMARY
                          )
                        }
                        className="flex items-center gap-3"
                      >
                        <span className="text-base font-bold text-[#353535]">
                          Primary
                        </span>
                        <Icon
                          name={
                            guardian.guardianType === GuardianTypeEnum.PRIMARY
                              ? Icons.CheckedBox
                              : Icons.UncheckBox
                          }
                        />
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          formik.setFieldValue(
                            `guardians[${index}].guardianType`,
                            GuardianTypeEnum.SUBSTITUTE
                          )
                        }
                        className="flex items-center gap-3"
                      >
                        <span className="text-base font-bold text-[#353535]">
                          Substitute
                        </span>
                        <Icon
                          name={
                            guardian.guardianType ===
                            GuardianTypeEnum.SUBSTITUTE
                              ? Icons.CheckedBox
                              : Icons.UncheckBox
                          }
                        />
                      </button>
                    </div>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 md:gap-10">
                  <div className="group relative z-0 mb-6 w-full">
                    <CustomSelect
                      options={RelationshipType}
                      selectedOption={{
                        value: guardian.relationship,
                        label: guardian.relationship
                      }}
                      handleOptionChange={option => {
                        formik.setFieldValue(
                          `guardians[${index}].relationship`,
                          option?.value
                        );
                      }}
                      label="Relationship"
                      name="relationship"
                      isRequired
                      isSearchable
                      className="h-[3.5rem] rounded-2xl"
                      error={
                        formik.touched.guardians
                          ? formik.errors.guardians?.[index] &&
                            formik.errors.guardians.length > 0
                            ? JSON.parse(
                                JSON.stringify(formik.errors.guardians[index])
                              ).relationship
                            : ''
                          : ''
                      }
                    />
                  </div>
                  <div className="group relative z-0 mb-6 w-full">
                    <CustomSelect
                      options={MaritalStatus}
                      selectedOption={{
                        value: guardian.maritalStatus,
                        label: guardian.maritalStatus
                      }}
                      handleOptionChange={option => {
                        formik.setFieldValue(
                          `guardians[${index}].maritalStatus`,
                          option?.value
                        );
                      }}
                      label="Marital Status"
                      name="maritalStatus"
                      isRequired
                      isSearchable
                      className="h-[3.5rem] rounded-2xl"
                      error={
                        formik.touched.guardians
                          ? formik.errors.guardians?.[index] &&
                            formik.errors.guardians.length > 0
                            ? JSON.parse(
                                JSON.stringify(formik.errors.guardians[index])
                              ).maritalStatus
                            : ''
                          : ''
                      }
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 md:gap-10">
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`guardians[${index}].address`}
                      placeholder=""
                      isRequired
                      value={guardian.address}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Home Address"
                      error={
                        formik.touched.guardians
                          ? formik.errors.guardians?.[index] &&
                            formik.errors.guardians.length > 0
                            ? JSON.parse(
                                JSON.stringify(formik.errors.guardians[index])
                              ).address
                            : ''
                          : ''
                      }
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`guardians[${index}].emailAddress`}
                      placeholder=""
                      isRequired
                      value={guardian.emailAddress}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Email Address"
                      error={
                        formik.touched.guardians
                          ? formik.errors.guardians?.[index] &&
                            formik.errors.guardians.length > 0
                            ? JSON.parse(
                                JSON.stringify(formik.errors.guardians[index])
                              ).emailAddress
                            : ''
                          : ''
                      }
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 md:gap-10">
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`guardians[${index}].phoneNumber`}
                      placeholder=""
                      isRequired
                      value={guardian.phoneNumber}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Phone Number"
                      error={
                        formik.touched.guardians
                          ? formik.errors.guardians?.[index] &&
                            formik.errors.guardians.length > 0
                            ? JSON.parse(
                                JSON.stringify(formik.errors.guardians[index])
                              ).phoneNumber
                            : ''
                          : ''
                      }
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`guardians[${index}].dateOfBirth`}
                      type="date"
                      isRequired
                      placeholder=""
                      value={guardian.dateOfBirth}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Date of Birth"
                      error={
                        formik.touched.guardians
                          ? formik.errors.guardians?.[index] &&
                            formik.errors.guardians.length > 0
                            ? JSON.parse(
                                JSON.stringify(formik.errors.guardians[index])
                              ).dateOfBirth
                            : ''
                          : ''
                      }
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                </div>

                {showRemoveButton(
                  index,
                  guardian.guardianId,
                  data!.length > 1
                ) && (
                  <div className="group relative z-0 mt-1 w-full">
                    {isDeleting ? (
                      <Loader height={20} width={20} type={'Wait'} />
                    ) : (
                      <button
                        type="button"
                        onClick={() =>
                          removeGuardian(index, guardian.guardianId)
                        }
                      >
                        <Icon name={Icons.Delete} />
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
            <div className="flex items-center justify-between gap-2">
              <div className="flex w-full items-center gap-2">
                <Icon name={Icons.Required} />
                <span className="text-[10px] text-[#7A7C87]">
                  Indicates Required
                </span>
              </div>
              <div className="group z-0 flex w-full items-center justify-end">
                <div
                  className="flex items-center gap-4"
                  onClick={addMoreGuardian}
                  role="button"
                  tabIndex={-1}
                >
                  <Icon name={Icons.AddCircleOutline} />
                  <span className="mt-2 text-base font-medium text-[#353535]">
                    Add Another Guardian
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-5 flex w-full items-end justify-between">
              <Button
                variant="outline"
                size="md"
                className="mt-1 w-[9rem] rounded p-[0.1rem] text-primary-900"
                label="Previous"
                type="button"
                onClick={() => onPrevious()}
              ></Button>
              <div className="mt-5 flex items-end justify-end gap-8">
                {parentPath !== 'dashboard' && (
                  <button
                    type="submit"
                    className="font-circular text-[15px] font-bold text-[#353535] underline"
                    onClick={() => {
                      onContinueLater();
                      setIsLater(true);
                    }}
                  >
                    Save and Continue Later
                  </button>
                )}
                <Button
                  variant="primary"
                  size="md"
                  className="mt-5 w-[9rem] rounded p-[0.1rem]"
                  label="Proceed"
                  type="submit"
                  disabled={!formik.isValid}
                  loading={isSubmitting || isLoading}
                ></Button>
              </div>
            </div>
          </form>
        </>
      )}
    </div>
  );
}
